import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Sparkles, CheckCircle2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const ISLANDS = ["Viti Levu", "Vanua Levu", "Mamanuca Islands", "Yasawa Islands", "Kadavu", "Taveuni", "Other"];

export default function LeadForm({ source = "unknown", variant = "card", title, subtitle }) {
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "+679",
        location_island: "Viti Levu",
        location_region: "",
        land_acres: "",
        asking_price_fjd: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm({ ...form, [k]: v });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/jv-leads", {
                ...form,
                land_acres: Number(form.land_acres),
                asking_price_fjd: Number(form.asking_price_fjd),
                source,
            });
            toast.success("Vinaka! Our deal team will reach out within 1 business day.");
            setDone(true);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <Card className="p-8 sm:p-10 bg-fiji-green/10 border-fiji-green/30 text-center" data-testid="lead-form-success">
                <div className="h-14 w-14 rounded-full bg-fiji-green/20 grid place-items-center mx-auto mb-4">
                    <CheckCircle2 className="h-8 w-8 text-fiji-green" />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold mb-2">Request received</h3>
                <p className="font-app text-muted-foreground max-w-md mx-auto">
                    Our deal team will review your land details and contact you within 1 business day with a free no-obligation Joint Venture valuation.
                </p>
            </Card>
        );
    }

    return (
        <Card data-testid="lead-form" className={`p-8 sm:p-10 ${variant === "dark" ? "bg-ocean-deep-grad text-white" : "bg-card"}`}>
            <div className="flex items-center gap-2 mb-3">
                <Sparkles className={`h-5 w-5 ${variant === "dark" ? "text-turquoise" : "text-coral"}`} />
                <span className={`text-xs uppercase tracking-[0.3em] font-app font-semibold ${variant === "dark" ? "text-turquoise" : "text-coral"}`}>
                    Free Joint Venture Valuation
                </span>
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold mb-2 leading-tight">
                {title || "Get a free Joint Venture valuation for your land."}
            </h3>
            <p className={`font-app text-sm mb-6 ${variant === "dark" ? "text-white/80" : "text-muted-foreground"}`}>
                {subtitle || "Tell us about your land. Our deal team will model the Joint Venture uplift and reach out within 1 business day. No commitment."}
            </p>

            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label className={variant === "dark" ? "text-white" : ""}>Full name</Label>
                        <Input data-testid="lead-name" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className={`mt-1.5 ${variant === "dark" ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : ""}`} />
                    </div>
                    <div>
                        <Label className={variant === "dark" ? "text-white" : ""}>Phone</Label>
                        <Input data-testid="lead-phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} className={`mt-1.5 ${variant === "dark" ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : ""}`} />
                    </div>
                </div>
                <div className="sm:col-span-2">
                    <Label className={variant === "dark" ? "text-white" : ""}>Email</Label>
                    <Input data-testid="lead-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={`mt-1.5 ${variant === "dark" ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : ""}`} />
                </div>
                <div>
                    <Label className={variant === "dark" ? "text-white" : ""}>Island</Label>
                    <Select value={form.location_island} onValueChange={(v) => update("location_island", v)}>
                        <SelectTrigger data-testid="lead-island" className={`mt-1.5 ${variant === "dark" ? "bg-white/10 border-white/20 text-white" : ""}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ISLANDS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className={variant === "dark" ? "text-white" : ""}>Region / town</Label>
                    <Input data-testid="lead-region" required value={form.location_region} onChange={(e) => update("location_region", e.target.value)} placeholder="Coral Coast, Savusavu…" className={`mt-1.5 ${variant === "dark" ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : ""}`} />
                </div>
                <div>
                    <Label className={variant === "dark" ? "text-white" : ""}>Land size (acres)</Label>
                    <Input data-testid="lead-acres" type="number" step="0.01" required value={form.land_acres} onChange={(e) => update("land_acres", e.target.value)} className={`mt-1.5 ${variant === "dark" ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : ""}`} />
                </div>
                <div>
                    <Label className={variant === "dark" ? "text-white" : ""}>Asking price expectations (FJD)</Label>
                    <Input data-testid="lead-price" type="number" required value={form.asking_price_fjd} onChange={(e) => update("asking_price_fjd", e.target.value)} className={`mt-1.5 ${variant === "dark" ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : ""}`} />
                </div>
                <div className="sm:col-span-2">
                    <Label className={variant === "dark" ? "text-white" : ""}>Anything we should know? (optional)</Label>
                    <Textarea data-testid="lead-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={3} className={`mt-1.5 ${variant === "dark" ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : ""}`} placeholder="Zoning, ownership, why you're considering a Joint Venture…" />
                </div>
                <div className="sm:col-span-2">
                    <Button type="submit" disabled={loading} data-testid="lead-submit-btn" className="w-full bg-coral hover:bg-coral-deep text-white font-app h-12 text-base">
                        {loading ? "Sending…" : <>Get my free Joint Venture valuation <ArrowRight className="ml-2 h-4 w-4" /></>}
                    </Button>
                    <div className={`mt-3 flex items-center gap-1.5 justify-center text-xs font-app ${variant === "dark" ? "text-white/60" : "text-muted-foreground"}`}>
                        <ShieldCheck className="h-3 w-3" /> No spam. We only use your details to prepare your valuation.
                    </div>
                </div>
            </form>
        </Card>
    );
}
